import React, { useState } from 'react';
import { Input, Button } from 'shared-components';
import { LoginShell } from './LoginShell';
import { LoginCard } from './LoginCard';
import Loading from '../../Abstracts/Components/Loading';
import AccountService from '../../Services/AccountService';

type Props = {
	hash: string;
};

const errorCopy: Record<string, string> = {
	invalid_credentials: 'Your current password was not correct.',
	rate_limited: 'Too many attempts. Please wait a moment before trying again.',
	not_authenticated: 'Please sign in again to change your password.',
	invalid_password: 'Your new password does not meet the requirements.',
};

export default function ChangePassword({ hash }: Props) {
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [formError, setFormError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [redirecting, setRedirecting] = useState(false);
	const passwordRequirementCopy = AccountService.getPasswordRequirementMessage();
	const formatPasswordErrors = (messages: string[]) =>
		`Password must ${messages
			.map((message) => message.replace(/^Password must /, '').replace(/\.$/, ''))
			.join(', ')}.`;

	const onSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setSubmitting(true);
		setError(null);
		setFormError(null);

		const validation = AccountService.validatePassword(newPassword);
		if (!validation.valid) {
			setFormError(formatPasswordErrors(validation.failures));
			setSubmitting(false);
			return;
		}
		if (newPassword !== confirmPassword) {
			setFormError('New password and confirmation do not match.');
			setSubmitting(false);
			return;
		}

		try {
			const response = await AccountService.changePassword(hash, currentPassword, newPassword);
			if ('error' in response && response.error) {
				setError(errorCopy[response.code] ?? 'Unable to change password.');
				return;
			}
			setRedirecting(true);
			if ('redirect' in response && response.redirect) {
				window.location.assign(response.redirect);
			}
		} catch (err) {
			console.error(err);
			setError('Unable to change password right now.');
		} finally {
			setSubmitting(false);
		}
	};

	if (redirecting) {
		return (
			<LoginShell>
				<LoginCard>
					<Loading message="Setting new password" />
				</LoginCard>
			</LoginShell>
		);
	}

	return (
		<LoginShell>
			<LoginCard>
				<h1 className="login-title">Change password</h1>
				<form className="login-form" onSubmit={onSubmit}>
					<Input
						id="current-password"
						label="Current password"
						type="password"
						value={currentPassword}
						onChange={(value) => setCurrentPassword(value)}
						className="login-field"
						inputProps={{ autoComplete: 'current-password', required: true }}
					/>
					<Input
						id="new-password"
						label="New password"
						type="password"
						value={newPassword}
						onChange={(value) => setNewPassword(value)}
						className="login-field"
						inputProps={{ autoComplete: 'new-password', required: true }}
					/>
					<Input
						id="confirm-password"
						label="Confirm new password"
						type="password"
						value={confirmPassword}
						onChange={(value) => setConfirmPassword(value)}
						className="login-field"
						inputProps={{ autoComplete: 'new-password', required: true }}
					/>
					{!formError && <p className="login-description">{passwordRequirementCopy}</p>}
					{formError ? (
						<p className="login-error" role="alert">
							{formError}
						</p>
					) : null}
					{error ? (
						<p className="login-error" role="alert">
							{error}
						</p>
					) : null}
					<Button
						className="login-button"
						color="primary"
						size="md"
						variant="contained"
						type="submit"
						disabled={submitting}
					>
						{submitting ? 'Saving...' : 'Save new password'}
					</Button>
				</form>
				<div className="login-footer">
					<a href={`/u/dashboard/${hash}`} className="login-link">
						Back to account
					</a>
				</div>
			</LoginCard>
		</LoginShell>
	);
}
